import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DateInput } from '@/shared/ui/DateInput/DateInput';
import { Select } from '@/shared/ui/Select/Select';
import { Input } from '@/shared/ui/Input/Input';
import { rateOptions, qualificationOptions, statusOptions, clubActiveOptions } from '@/shared/constants/userEnums';
import { dateFormatForBack } from '@/shared/lib/utils/dateFormat';
import styles from './EditableCell.module.scss';
import { InputTel } from '@/shared/ui/InputTel/InputTel';
import { phoneNumberFormat, phoneNumberFormatForInput } from '@/shared/lib/utils/phoneNumberFormat';
import { getAge } from '@/shared/lib/utils/getAge';

const optionsMap: Record<string, { value: string; label: string }[]> = {
  rate: rateOptions,
  qualification: qualificationOptions,
  status: statusOptions,
  isActive: clubActiveOptions,
};

interface EditableCellProps {
  value: string | null;
  field: string;
  type: 'string' | 'date' | 'select' | 'phone';
  onSave: (value: string | null) => Promise<void>;
  disabled?: boolean;
  clubType?: 'individual' | 'legal';
}

export const EditableCell = ({ value: initialValue, field, type, onSave, disabled, clubType }: EditableCellProps) => {
  const normalizePropValue = React.useCallback(
    (value: string | null) => {
      if (!value) {
        return '';
      }
      if (field === 'loginPrefix') {
        return value.trim().toUpperCase();
      }
      if (field === 'contactPhone') {
        return phoneNumberFormat(value);
      }
      return value;
    },
    [field],
  );

  const computeDisplayValue = React.useCallback(
    (value: string | null) => {
      if (type === 'phone') {
        return phoneNumberFormatForInput(value ?? '');
      }
      return value ?? '';
    },
    [type],
  );

  const defaultValue = React.useMemo(() => computeDisplayValue(initialValue), [computeDisplayValue, initialValue]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      value: defaultValue,
    },
  });

  const prevInitialRef = React.useRef(defaultValue);
  const prevPropValueRef = React.useRef(normalizePropValue(initialValue));

  const requiredFields = new Set<string>(['firstName', 'nickname']);

  useEffect(() => {
    const normalizedProp = normalizePropValue(initialValue);
    if (prevPropValueRef.current !== normalizedProp) {
      const nextDisplayValue = computeDisplayValue(initialValue);
      reset({ value: nextDisplayValue });
      prevInitialRef.current = nextDisplayValue;
      prevPropValueRef.current = normalizedProp;
    }
  }, [initialValue, reset, computeDisplayValue, normalizePropValue]);

  const processAndSave = async (data: { value: string }) => {
    const rawValue = data.value ?? '';
    let normalizedValue = rawValue;

    if (field === 'loginPrefix') {
      const trimmed = rawValue.trim();
      if (!trimmed || trimmed.length > 3) {
        return;
      }
      normalizedValue = trimmed.toUpperCase();
    } else if (field === 'contactPhone') {
      if (!rawValue) {
        normalizedValue = '';
      } else {
        const formatted = phoneNumberFormat(rawValue);
        if (!/^\+7\d{10}$/.test(formatted)) {
          return;
        }
        normalizedValue = formatted;
      }
    }

    const normalizedInitialValue = (() => {
      if (!initialValue) {
        return '';
      }
      if (field === 'loginPrefix') {
        return initialValue.trim().toUpperCase();
      }
      if (field === 'contactPhone') {
        return phoneNumberFormat(initialValue);
      }
      return initialValue;
    })();

    if (normalizedValue === normalizedInitialValue) {
      return;
    }

    let finalValue: string | null = normalizedValue === '' ? null : normalizedValue;
    const displayValue = normalizedValue;
    if (type === 'date' && finalValue) {
      finalValue = dateFormatForBack(finalValue);
    }

    try {
      await onSave(finalValue);
      const nextDisplayValue = computeDisplayValue(type === 'date' ? displayValue : finalValue);
      reset({ value: nextDisplayValue });
      prevInitialRef.current = nextDisplayValue;
    } catch (e) {
      reset({ value: prevInitialRef.current });
    }
  };

  const renderInput = ({ field: { value, onChange, name, onBlur } }: any) => {
    const hasError = !!errors.value;

    switch (type) {
      case 'date':
        return (
          <DateInput
            disabled={disabled}
            fullWidth={field === 'birthDate'}
            inputRadius
            value={value}
            onChange={onChange}
            onBlur={() => handleSubmit(processAndSave)()}
            name={name}
            error={hasError ? ' ' : ''}
          />
        );
      case 'select':
        return (
          <Select
            value={value}
            options={optionsMap[field]}
            onChange={(newValue) => {
              onChange(newValue);
              handleSubmit(() => processAndSave({ value: newValue }))();
            }}
          />
        );
      case 'phone':
        return (
          <InputTel
            disabled={disabled}
            value={value}
            onChange={onChange}
            onBlur={() => handleSubmit(processAndSave)()}
            placeholder={'+7 900 000-00-00'}
            error={hasError ? ' ' : ''}
            baseAddon={false}
            size={'compact'}
          />
        );
      case 'string':
      default:
        return (
          <Input
            disabled={disabled}
            value={value}
            onChange={onChange}
            onBlur={() => handleSubmit(processAndSave)()}
            name={name}
            error={hasError ? ' ' : ''}
            baseAddon={false}
            maxLength={field === 'loginPrefix' ? 3 : undefined}
            className={styles.stringInput}
          />
        );
    }
  };

  const validateByField = (val: string | null): boolean => {
    const value = val ?? '';

    if (requiredFields.has(field) && value.trim() === '') {
      return false;
    }

    const nameRegex = /^(?:[А-Яа-яёЁ\s‘’'-]+|[a-zA-Z\s‘’'-]+)$/;
    const nicknameRegex = /^[a-zA-Zа-яА-ЯёЁ0-9-]+$/;
    const vkRegex = /^https:\/\/vk\.(?:ru|com)\/.+/;

    switch (field) {
      case 'lastName':
        if (clubType === 'individual') {
          if (!value.trim()) return false;
          return value.length >= 2 && value.length <= 32 && nameRegex.test(value);
        }
        if (!clubType) {
          if (!value.trim()) return false;
          return value.length >= 2 && value.length <= 32 && nameRegex.test(value);
        }
        if (!value) return true;
        return value.length >= 2 && value.length <= 32 && nameRegex.test(value);
      case 'firstName':
        if (!value.trim()) return false;
        return value.length >= 2 && value.length <= 16 && nameRegex.test(value);
      case 'patronymic':
        if (clubType === 'individual') {
          if (!value.trim()) return false;
          return value.length >= 5 && value.length <= 32 && nameRegex.test(value);
        }
        if (!value) return true;
        return value.length >= 5 && value.length <= 32 && nameRegex.test(value);
      case 'birthDate': {
        if (!value) {
          if (clubType === 'individual') return false;
          return true;
        }
        if (!/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
          return false;
        }
        const age = getAge(value);
        if (age < 0 || age > 99) return false;
        if (age < 8) return false;
        return true;
      }
      case 'legalName':
        if (clubType === 'legal') {
          if (!value.trim()) return false;
          return value.length >= 2 && value.length <= 32 && nameRegex.test(value);
        }
        if (!value) return true;
        return value.length <= 32 && nameRegex.test(value);
      case 'payerInn':
        if (!value) {
          if (clubType === 'legal') return false;
          return true;
        }
        return /^\d{10}$/.test(value);
      case 'nickname':
        return value.length <= 16 && nicknameRegex.test(value);
      case 'loginPrefix':
        if (!value) {
          return false;
        }
        return value.length <= 3;
      case 'contactPhone':
        if (!value) {
          return true;
        }
        return /^\+7\d{10}$/.test(phoneNumberFormat(value));
      case 'vkLink':
        if (!value) return true;
        return vkRegex.test(value);
      default:
        return true;
    }
  };

  return (
    <form>
      <Controller
        name="value"
        control={control}
        rules={{
          validate: validateByField,
        }}
        render={renderInput}
      />
    </form>
  );
};
